import React, { useCallback, useContext, useEffect, useMemo } from "react";
import { isUserEditableNode } from "../../../../Components/Tree/TreeUtils";
import { Controller, useForm, useWatch } from "react-hook-form";
import { resourceTypes } from "../../../FindingsUtils";
import { useApolloClient, useQuery } from "@apollo/client";
import {
  CREATE_GROUP,
  EDIT_GROUP,
  GET_GROUP_BY_ID,
  parseGroupData,
} from "../ScopesApi";
import { showToast } from "../../../../Forms/Dialogs/Toast/Toast";
import "./CreateGroup.scss";
import { LS_SCOPE } from "../../FilterUtils";
import Tree from "../../../../Components/Tree/Tree";
import { getQueuePluralsByCount } from "../../../../AppView/AppViewUtils";
import LocationsTree from "../../../../AppView/ScopeManagement/LocationsTree/LocationsTree";
import FilterInQueuePopup from "../../FilterInQueuePopup/FilterInQueuePopup";
import {
  TextButton,
  TextInput,
  useMutationWrapper,
  ModalContext,
  ModalButtonsContainer,
  showDialog,
  showErrorDialog,
  REQUIRED_ERROR_MESSAGE,
} from "@SeemplicityDev/shared";
import type {
  ScopeNode,
  SelectedScope,
  ScopesStateType,
} from "../../../../AppView/ScopeManagement/ScopeProvider/ScopeProvider";
import type { TreeNode } from "../../../../Components/Tree/types";

// A child node selected for the group (subset of a tree node).
interface GroupChild {
  level?: number;
  id: string;
  type?: string;
  parentId?: string | null;
}

// React Hook Form values managed by this form.
interface CreateGroupFormValues {
  name?: string;
  location?: TreeNode | null;
  group_children?: GroupChild[];
}

// Result shape returned by the create/edit group mutations.
interface SaveGroupResult {
  data?: {
    create_scope_group?: {
      ok?: boolean;
      group_id?: string;
    } | null;
    edit_scope_group?: {
      ok?: boolean;
      effected_queues?: string[];
    } | null;
  } | null;
}

// Variables sent to the create/edit group mutations.
interface SaveGroupVariables {
  name?: string;
  parent_group?: string;
  child_scopes: string[];
  child_groups: string[];
  overwrite?: boolean;
  group_id?: string;
}

export interface CreateGroupProps {
  groupId?: string;
  groupName?: string;
  root?: ScopeNode[] | null;
  updateScopeState: (state: Partial<ScopesStateType>) => void;
  searchTree: (node: ScopeNode, id: string) => ScopeNode | null;
  selectedScope?: SelectedScope | null;
}

const CreateGroup: React.FC<CreateGroupProps> = ({
  groupId,
  groupName,
  root,
  updateScopeState,
  searchTree,
  selectedScope,
}) => {
  const methods = useForm<CreateGroupFormValues>({
    mode: "all",
    reValidateMode: "onChange",
  });
  const {
    handleSubmit,
    control,
    formState: { errors, isValid, isDirty },
    getValues,
    reset,
    clearErrors,
  } = methods;
  const { closeModal } = useContext(ModalContext);
  const client = useApolloClient();

  const [createGroupMutation] = useMutationWrapper<
    NonNullable<SaveGroupResult["data"]>
  >(CREATE_GROUP, {
    refetchQueries: ["scopes"],
  });
  const [editGroupMutation] = useMutationWrapper<
    NonNullable<SaveGroupResult["data"]>
  >(EDIT_GROUP, {
    refetchQueries: ["scopes"],
  });
  const watchLocationValue = useWatch({
    control,
    name: "location",
  });

  const { data: groupData, loading: isLoadingGroupData } = useQuery(
    GET_GROUP_BY_ID,
    {
      variables: { group_id: groupId },
      skip: !groupId,
      fetchPolicy: "network-only",
    }
  );

  useEffect(() => {
    if (!!groupData && !!groupId) {
      const data = parseGroupData(groupData?.scope_group_by_id?.group_data);
      if (!data) {
        return;
      }
      const groupLocation = searchTree(
        root?.[0] as ScopeNode,
        data.parent_group?.id as string
      );
      const groupNode = searchTree(root?.[0] as ScopeNode, groupId);
      const groupChildren: GroupChild[] =
        groupNode?.children
          ?.map((node) => {
            return {
              // ScopeNode carries a runtime `level` not in its declared type.
              level: (node as { level?: number }).level,
              id: node.id,
              type: node.type,
            };
          })
          ?.sort((a, b) => a.id.localeCompare(b.id)) || [];

      reset({
        name: data?.name,
        location: (groupLocation || root?.[0]) as unknown as TreeNode | null,
        group_children: groupChildren,
      });
    }
  }, [groupData, groupId]);

  const alreadySavedScopes = useMemo<GroupChild[]>(() => {
    if (!!groupData && !!groupId) {
      const data = parseGroupData(groupData?.scope_group_by_id?.group_data);
      if (!data) {
        return [];
      }

      const groups = (data.child_groups ?? []).map((item) => {
        return {
          id: item.id,
          type: resourceTypes.GROUP,
        };
      });

      const scopes = (data.child_scopes ?? []).map((item) => {
        return {
          id: item.id,
          type: resourceTypes.SCOPE,
        };
      });

      return [...groups, ...scopes];
    }
    return [];
  }, [groupData, groupId]);

  const onSubmit = (data: CreateGroupFormValues, overwrite?: boolean) => {
    const groupData: SaveGroupVariables = {
      name: data.name,
      parent_group: data?.location?.id,
      child_scopes:
        data?.group_children
          ?.filter((node) => node.type === resourceTypes.SCOPE)
          ?.map((node) => node.id) || [],
      child_groups:
        data?.group_children
          ?.filter((node) => node.type === resourceTypes.GROUP)
          ?.map((node) => node.id) || [],
      overwrite,
    };

    if (!!groupId) {
      groupData.group_id = groupId;
    }

    saveGroup(
      !groupId ? createGroupMutation : editGroupMutation,
      groupData,
      overwrite
    );
  };

  const validateOnSave = () => {
    handleSubmit((data) => onSubmit(data, false))();
  };

  const saveGroup = (
    groupMutation: typeof createGroupMutation,
    groupData: SaveGroupVariables,
    overwrite?: boolean
  ) => {
    groupMutation({ variables: groupData })
      .then((res) => {
        let level: number | undefined;
        // location changed.
        if (watchLocationValue?.id) {
          // inside a group or under the root if level is 0.
          level = (watchLocationValue?.level as number) + 1 || 1;
        } else {
          // editing active group without change in the location
          if (groupId === selectedScope?.value?.id) {
            level = selectedScope?.level;
          } else {
            // creating new group from active group context.
            level = (selectedScope?.level ?? 0) + 1;
          }
        }

        if (!!res.data?.create_scope_group?.ok) {
          const hasChildren =
            !!groupData?.child_scopes?.length ||
            !!groupData?.child_groups?.length;

          const updatedScope: SelectedScope = {
            label: groupData.name,
            level,
            value: {
              query: undefined,
              id: res.data?.create_scope_group?.group_id,
              editable: true,
              type: resourceTypes.GROUP as SelectedScope["value"]["type"],
            },
          };

          if (hasChildren) {
            updateScopeState({
              isNewScope: false,
              isDirty: false,
              scopeToExecute: null,
              selectedScope: updatedScope,
            });
            localStorage.setItem(LS_SCOPE, JSON.stringify(updatedScope));
          }

          closeModal();
          showToast({
            message: (
              <div className={"modal-text"}>
                The Group{" "}
                <span className={"modal-live-text"}>{groupData?.name}</span> was
                saved successfully
              </div>
            ),
            "data-testid": "group-created-success-toast",
          });
        } else if (
          !overwrite &&
          !res?.data?.edit_scope_group?.ok &&
          res?.data?.edit_scope_group?.effected_queues?.length
        ) {
          showDialog({
            title: "Save Group",
            type: "warning",
            className: "save-warning",
            alignLeft: true,
            description: (
              <div>
                The Scope Group{" "}
                <span className={"modal-live-text"}>"{groupData.name}"</span> is
                part of{" "}
                {getQueuePluralsByCount(
                  res?.data?.edit_scope_group?.effected_queues?.length
                ) === "queue"
                  ? " an existing queue"
                  : " existing queues"}
                .
              </div>
            ),
            message: (
              <FilterInQueuePopup
                type={"group"}
                queuesList={res?.data?.edit_scope_group?.effected_queues?.map(
                  (queue: string) => ({ name: queue })
                )}
              />
            ),

            buttons: [
              {
                label: "Cancel",
              },
              {
                buttonStyle: "primary",
                "data-testid": "save-and-update-queue",
                label: "Save and update queue",
                onClick: () => {
                  groupData.overwrite = true;
                  saveGroup(editGroupMutation, groupData, true);
                },
              },
            ],
          });
        } else if (!!res.data?.edit_scope_group?.ok) {
          const updatedScope: SelectedScope = {
            label: groupData.name,
            level,
            value: {
              query: undefined,
              id: groupId,
              editable: true,
              type: resourceTypes.GROUP as SelectedScope["value"]["type"],
            },
          };
          localStorage.setItem(LS_SCOPE, JSON.stringify(updatedScope));

          updateScopeState({
            scopeToExecute: null,
            selectedScope: updatedScope,
            isDirty: false,
            isNewScope: false,
          });

          client.refetchQueries({
            include: ["GetFindings", "GetFindingsByGroup"],
          });
          closeModal();
          showToast({
            message: (
              <div className={"modal-text"}>
                The Group{" "}
                <span className={"modal-live-text"}>{groupData.name}</span> was
                updated successfully
              </div>
            ),
            "data-testid": "group-updated-success-toast",
          });
        } else {
          showErrorDialog({
            message: "An error occurred while trying to create a group",
          });
        }
      })
      .catch((err: Error) => showErrorDialog({ message: err.message }));
  };

  const treeData = useMemo<TreeNode[]>(() => {
    const newRoot: ScopeNode = { ...(root?.[0] as ScopeNode) };
    if (newRoot?.children?.length) {
      newRoot.children = newRoot?.children?.filter(isUserEditableNode);
    }

    return [newRoot] as unknown as TreeNode[];
  }, [root]);

  const nodeHasWarning = (id: string | undefined = undefined) => {
    const { group_children } = getValues();
    let isChildrenListHasWarn;

    isChildrenListHasWarn = group_children?.find(
      (child) =>
        child.id === id &&
        child.parentId !== root?.[0]?.id &&
        child.parentId !== null &&
        !alreadySavedScopes.find((scope) => scope.id === id)
    );

    return !!isChildrenListHasWarn;
  };

  const treeHasWarning = useCallback(() => {
    const { group_children } = getValues();
    let isChildrenListHasWarn;

    isChildrenListHasWarn = group_children?.some((child) =>
      nodeHasWarning(child.id)
    );

    return !!isChildrenListHasWarn;
  }, [nodeHasWarning]);

  return (
    <form
      className="create-group-form-wrapper"
      onSubmit={handleSubmit((data) => onSubmit(data))}
    >
      <div className="modal-scroll-area">
        <div className="create-group-form-content">
          <Controller
            name={`name`}
            render={({ field: { value, onChange, onBlur, ref } }) => {
              return (
                <TextInput
                  data-testid={`group-name`}
                  labelTop
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    onChange(e)
                  }
                  inputRef={ref}
                  onBlur={onBlur}
                  value={value}
                  error={errors?.name ? errors.name : null}
                  label={"Name"}
                  required
                  placeholder={"Type a name"}
                  className="create-group-name"
                />
              );
            }}
            control={control}
            rules={{
              required: REQUIRED_ERROR_MESSAGE,
            }}
          />

          <Controller
            name={`location`}
            render={({ field: { value, onChange, onBlur } }) => {
              return (
                <LocationsTree
                  name={`location`}
                  onChange={(node) => onChange(node)}
                  error={errors?.location ? errors.location : null}
                  label={"Location"}
                  value={value}
                  isLoading={isLoadingGroupData}
                  root={root as unknown as TreeNode[] | null}
                  labelTop
                  required
                  popoverClassName={"tree-in-new-group-menu"}
                />
              );
            }}
            rules={{
              required: REQUIRED_ERROR_MESSAGE,
              validate: {
                validChildren: (value) => {
                  const { group_children } = getValues();

                  const isChildrenListContainsLocation = group_children?.filter(
                    (child) => child.id === value?.id
                  )?.length;

                  const isLocationContainsSelf = value?.id === groupId;

                  if (isLocationContainsSelf) {
                    return "This is already the current location. Please choose a different one.";
                  }

                  if (isChildrenListContainsLocation) {
                    return `${value?.name} can't be both the Location and a member of the Group.`;
                  }

                  clearErrors("group_children");
                  return (
                    !isChildrenListContainsLocation && !isLocationContainsSelf
                  );
                },
              },
            }}
            control={control}
          />

          <div className={`group-children-wrapper`}>
            <div className={`input-wrap has-label-top has-no-margin`}>
              <div className={`group-children-container`}>
                <Controller
                  name={`group_children`}
                  render={({ field: { onChange } }) => (
                    <Tree
                      label={"Select Scopes for this Group"}
                      required={!!groupId}
                      data={!!root ? treeData : null}
                      isSelectableNode={(node) =>
                        node.type !== resourceTypes.ROOT
                      }
                      onChange={(nodes) =>
                        onChange(
                          nodes
                            .map((node) => {
                              return {
                                level: node.level,
                                id: node.id,
                                type: node.type,
                              };
                            })
                            .sort((a, b) =>
                              (a.id as string).localeCompare(b.id as string)
                            )
                        )
                      }
                      nodeOperations={{
                        onEdit: () => {},
                        onSelect: () => {},
                        onWarning: nodeHasWarning,
                      }}
                      data-testid={"group-children-tree"}
                      withCheckbox
                      preCheckedNodes={
                        alreadySavedScopes as unknown as TreeNode[]
                      }
                      error={
                        errors && errors.group_children
                          ? errors.group_children
                          : null
                      }
                      isSearchable
                      hideLabelHelper={false}
                    />
                  )}
                  control={control}
                  rules={{
                    required: !!groupId ? REQUIRED_ERROR_MESSAGE : false,
                    validate: {
                      validChildren: (value) => {
                        const { location } = getValues();

                        const isChildrenListContainsLocation = value?.filter(
                          (child) => child.id === location?.id
                        )?.length;

                        const isLocationContainsSelf = value?.filter(
                          (child) => child.id === groupId
                        )?.length;

                        if (isLocationContainsSelf) {
                          return `A Group cannot be a child of itself. \r\n Please change your selection, or the location of the Group.`;
                        }

                        if (isChildrenListContainsLocation) {
                          return `You chose ${location?.name} as your Location, it cannot also appear under your group. \r\n ֿPlease change your selection.`;
                        }

                        clearErrors("location");
                        return (
                          !isChildrenListContainsLocation &&
                          !isLocationContainsSelf
                        );
                      },
                    },
                  }}
                />
              </div>
            </div>
          </div>
          {treeHasWarning() && (
            <div className={`warning-wrapper`}>
              <div className={`warning-message-container`}>
                <i className={`seem-icon seem-icon-warning`} />
                <span>
                  Scopes that are already associated with a Group will be moved
                  to the new Group
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      <ModalButtonsContainer>
        <TextButton
          data-testid={"create-group-cancel"}
          type="button"
          label={"Cancel"}
          onClick={closeModal}
        />
        <TextButton
          data-testid={"create-group"}
          label={!groupId ? "Create" : "Update"}
          buttonStyle={"primary"}
          disabled={!isValid || !isDirty}
          type={"button"}
          onClick={() => validateOnSave()}
        />
      </ModalButtonsContainer>
    </form>
  );
};

export default CreateGroup;
